import { ModuleWithProviders } from '@angular/core';
import { RouterModule } from '@angular/router';
import { applicationResolver } from '@shared/resolvers/application.resolver';
import { businessActivitySurveyResolver } from '@shared/resolvers/business-activity-survey.resolver';
import { companyResolver } from '@shared/resolvers/company.resolver';
import { contactsResolver } from '@shared/resolvers/contacts.resolver';
import { countryListResolver } from '@shared/resolvers/country-list.resolver';
import { epaStatusResolver } from '@shared/resolvers/epa-status.resolver';
import { expectedActivitySurveyResolver } from '@shared/resolvers/expected-activity-survey.resolver';
import { geoLocationCountryResolver } from '@shared/resolvers/geo-location-country.resolver';
import { AdminComponent } from './admin/admin.component';
import { ApplicantPreDotfilePageComponent } from './applicant-pre-dotfile-page/applicant-pre-dotfile-page.component';
import { verificationPortalResolver } from './applicant-pre-dotfile-page/verification-portal.resolver';
import { ApplicantPreIdvPageComponent } from './applicant-pre-idv-page/applicant-pre-idv-page.component';
import { applicantPreIdvResolver } from './applicant-pre-idv-page/applicant-pre-idv.resolver';
import { BeneficialOwnerChoiceComponent } from './beneficial-owner-choice/beneficial-owner-choice.component';
import { BeneficialOwnersComponent } from './beneficial-owners/beneficial-owners.component';
import { CompanyDescriptionComponent } from './company-step/company-description/company-description.component';
import { CompanyInfoComponent } from './company-step/company-info/company-info.component';
import { CompanySearchComponent } from './company-step/company-search/company-search.component';
import { InternationalActivityComponent } from './company-step/international-activity/international-activity.component';
import { RegisteredAddressComponent } from './company-step/registered-address/registered-address.component';
import { SalesChannelComponent } from './company-step/sales-channel/sales-channel.component';
import { TradingAddressComponent } from './company-step/trading-address/trading-address.component';
import { DirectorComponent } from './director/director.component';
import { CashWithdrawalComponent } from './expected-activity-step/cash-withdrawal/cash-withdrawal.component';
import { ExternalDepositComponent } from './expected-activity-step/external-deposit/external-deposit.component';
import { MonthlySpendComponent } from './expected-activity-step/monthly-spend/monthly-spend.component';
import { NumberOfCardsComponent } from './expected-activity-step/number-of-cards/number-of-cards.component';
import { UseCaseComponent } from './expected-activity-step/use-case/use-case.component';
import { RegistrationComponent } from './registration.component';
import { completedApplicationGuard } from './registration.guard';
import { SignupComponent } from './signup-step/signup/signup.component';
import { TermsConditionComponent } from './terms-condition/terms-condition.component';

export const registrationRouting: ModuleWithProviders<RouterModule> = RouterModule.forChild([
    {
        path: 'registration',
        component: RegistrationComponent,
        canActivateChild: [completedApplicationGuard],
        children: [
            {
                path: 'welcome/signup',
                component: SignupComponent,
                data: {
                    pageName: 'Signup'
                },
                resolve: {
                    application: applicationResolver,
                    geoLocationCountry: geoLocationCountryResolver,
                    countryList: countryListResolver
                }
            }, {

                path: 'company/company-search',
                component: CompanySearchComponent,
                data: {
                    pageName: 'Company Search'
                },
                resolve: {
                    company: companyResolver
                }
            }, {
                path: 'company/info',
                component: CompanyInfoComponent,
                data: {
                    pageName: 'Company Identifiers'
                },
                resolve: {
                    company: companyResolver
                }
            }, {
                path: 'company/registered-address',
                component: RegisteredAddressComponent,
                data: {
                    pageName: 'Registered Address'
                },
                resolve: {
                    company: companyResolver,
                    countryList: countryListResolver
                }
            }, {
                path: 'company/trading-address',
                component: TradingAddressComponent,
                data: {
                    pageName: 'Trading Address'
                },
                resolve: {
                    company: companyResolver,
                    countryList: countryListResolver
                }
            }, {
                path: 'company/sales-channel',
                component: SalesChannelComponent,
                data: {
                    pageName: 'Sales Channel'
                },
                resolve: {
                    businessActivitySurvey: businessActivitySurveyResolver
                }
            }, {
                path: 'company/international-activity',
                component: InternationalActivityComponent,
                data: {
                    pageName: 'International Activity'
                },
                resolve: {
                    company: companyResolver,
                    businessActivitySurvey: businessActivitySurveyResolver,
                    countryList: countryListResolver
                }
            }, {
                path: 'company/business-description',
                component: CompanyDescriptionComponent,
                data: {
                    pageName: 'Business Description'
                },
                resolve: {
                    businessActivitySurvey: businessActivitySurveyResolver
                }
            }, {
                path: 'director',
                data: {
                    pageName: "Director Details"
                },
                component: DirectorComponent,
                resolve: {
                    company: companyResolver,
                    contacts: contactsResolver,
                    countryList: countryListResolver
                }
            },
            {
                path: 'account',
                data: {
                    pageName: "Account Admin"
                },
                component: AdminComponent,
                resolve: {
                    company: companyResolver,
                    contacts: contactsResolver,
                    countryList: countryListResolver
                }
            },
            {
                path: 'beneficial-owners',
                data: {
                    pageName: "Beneficial Owners"
                },
                component: BeneficialOwnerChoiceComponent,
                resolve: {
                    application: applicationResolver
                }
            },
            {
                path: 'beneficial-owners-details',
                data: {
                    pageName: "Beneficial Owners Details"
                },
                component: BeneficialOwnersComponent,
                resolve: {
                    contacts: contactsResolver,
                    countryList: countryListResolver
                }
            }, {
                path: 'expected-activity/cash-withdrawal',
                component: CashWithdrawalComponent,
                data: {
                    pageName: "Cash Withdrawal"
                },
                resolve: {
                    expectedActivitySurvey: expectedActivitySurveyResolver
                }
            }, {
                path: 'expected-activity/external-deposit',
                component: ExternalDepositComponent,
                data: {
                    pageName: "External Deposit"
                },
                resolve: {
                    expectedActivitySurvey: expectedActivitySurveyResolver
                }
            }, {
                path: 'expected-activity/monthly-spend',
                component: MonthlySpendComponent,
                data: {
                    pageName: "Monthly Spend"
                },
                resolve: {
                    expectedActivitySurvey: expectedActivitySurveyResolver
                }
            }, {
                path: 'expected-activity/number-of-cards',
                component: NumberOfCardsComponent,
                data: {
                    pageName: "Number of Cards"
                },
                resolve: {
                    expectedActivitySurvey: expectedActivitySurveyResolver
                }
            }, {
                path: 'expected-activity/use-case',
                component: UseCaseComponent,
                data: {
                    pageName: "Use Case"
                },
                resolve: {
                    expectedActivitySurvey: expectedActivitySurveyResolver
                }
            }, {
                path: 'terms-conditions',
                data: {
                    pageName: "Terms and Conditions"
                },
                component: TermsConditionComponent,
                resolve: {
                    application: applicationResolver,
                    isEpa: epaStatusResolver
                }
            },
            {
                path: 'verify-your-identity',
                data: {
                    pageName: "Identity Verification",
                    canAccessAfterTerms: true
                },
                component: ApplicantPreIdvPageComponent,
                resolve: {
                    applicantPreIdvData: applicantPreIdvResolver
                }
            },
            {
                path: 'verification-portal',
                data: {
                    pageName: "Identity Verification Portal",
                    canAccessAfterTerms: true
                },
                component: ApplicantPreDotfilePageComponent,
                resolve: {
                    verificationPortalData: verificationPortalResolver
                }
            }
        ]
    }
]);
